<template>
  <div class="dashboard-see-more-modal">
    <base-modal hide-footer modal-id="dashboard-see-more-modal" size="lg" :is-modal-open="isOpen" @open-modal="onOpenModal" @hide="hideModal">
      <template #modal-header>
        <div class="see-more-header">
          <div class="see-more-channel-container">
            <component :is="resolveChannelIcon()" style="height: 29px" />
            {{ resolveChannelTitle() }}
          </div>
          <div class="see-more-day-container">{{ day }}</div>
        </div>
      </template>
      <template #modal-body>
        <div class="all-campaign-summary-container">
          <div v-for="item in listAllCampaignSummary" :key="item.title" class="all-campaign-summary">
            <h5>{{ item.title }}</h5>
            <h5><transition-number :cubic-bezier-number="item.value" /></h5>
            <p style="color: #606a6c">{{ item.category }}</p>
          </div>
        </div>
        <div class="d-flex justify-content-center" style="min-height: 301px">
          <template v-if="!loading.allCampaignSummary && !loading.campaignListSummary">
            <base-table
              class="base-table list-campaign-summary-table"
              style="width: 100%"
              :columns="columns"
              :data="listCampaignSummary"
              responsive
              hover
            >
              <template #cell(name)="data">
                <span
                  style="color: #007fff; cursor: pointer"
                  @click="onClickCampaignName(data.item)"
                  v-bt-tooltips="{ bgColor: '#007fff', color: '#fff' }"
                  title="Go to campaign dashboard."
                  >{{ data.item.name }}</span
                >
              </template>
              <template #cell(sent)="data">
                <div>{{ data.item.sent }}</div>
              </template>
              <template #cell(complete)="data">
                <div>{{ data.item.complete }}</div>
              </template>
              <template #cell(fail)="data">
                <div>{{ data.item.fail }}</div>
              </template>
              <template #cell(updated_dt)="data">
                <div>{{ convertDate(data.item.updated_dt) }}</div>
              </template>
            </base-table>
          </template>
          <template v-else>
            <div>
              <loader-spinner />
            </div>
          </template>
        </div>

        <div class="d-flex justify-content-end mb-1">
          <Pagination
            v-model="pagination.currentPage"
            :totalRows="pagination.totalRows"
            :perPages="pagination.perPage"
            @update:model-value="fetchCampaignListSummary"
          />
        </div>
        <div class="d-flex justify-content-center">
          <button class="btn button-close" @click="hideModal">close</button>
        </div>
      </template>
    </base-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, reactive } from 'vue';
import { CHANNEL_VALUE } from '../../../../../communication/pages/main/constants/channel';
import { useRouter } from 'vue-router';
// API
import api from '@/services/api';
import analyticsModel from '@/models/analytics';
import useValidationModal from '@/views/components/modal/hooks/useValidationModal';

// component
import BaseModal from '@/views/components/modal/BaseModal.vue';
import TransitionNumber from '@/views/components/transition-number/TransitionNumber.vue';
import BaseTable from '@/views/components/table/base-table/BaseTable.vue';
import Pagination from '@/views/components/pagination/Pagination.vue';
import LoaderSpinner from '@/views/components/loader/loader-spinner.vue';
import useConvertDate from '@/utils/hooks/useConvertDate';

// icon
import ChannelLineIcon from '@/assets/icons/modules/communication/all-channel-tab/LineOaIcon.vue';
import ChannelEDMIcon from '@/assets/icons/modules/communication/all-channel-tab/EmailIcon.vue';
import ChannelSmsIcon from '@/assets/icons/modules/communication/all-channel-tab/SMSIcon.vue';

const emits = defineEmits(['hide']);

type ChannelType = 1 | 2 | 3 | 4; // 1=sms 2=email 3=line 4=mobile-push
interface Props {
  isOpen: boolean;
  selectedChannel: ChannelType;
  day: string;
  selectedDay: number;
  selectedHourRange: string;
  startDateTime: string;
  endDateTime: string;
  hasDateFilter: string;
}

interface SummaryLists {
  title: string;
  value: number;
  category: string;
}

const props = defineProps<Props>();

const router = useRouter();
const { fetchAnalyticsChartCampaignSummaryModel, fetchAnalyticsChartCampaignSummaryListModel } = analyticsModel();
const { openDefaultErrorModal, openErrorModal, openWarningModal, openSuccessModal } = useValidationModal();
const { convertDate, convertDateText } = useConvertDate();

const listAllCampaignSummary: Ref<SummaryLists[]> = ref([]);
const listCampaignSummary: Ref<Communication.DashboardByChannel.Response.CampaignListSummary[]> = ref([]);

const loading: { allCampaignSummary: boolean; campaignListSummary: boolean } = reactive({
  allCampaignSummary: false,
  campaignListSummary: false,
});

const columns: BaseTable.TableColumn[] = reactive([
  {
    key: 'no',
    label: 'No',
  },
  {
    key: 'name',
    label: 'Campaign Name',
  },
  {
    key: 'sent',
    label: 'Sent',
  },
  {
    key: 'complete',
    label: 'Complete',
  },
  {
    key: 'fail',
    label: 'Fail',
  },
  {
    key: 'updated_dt',
    label: 'Last Update',
  },
]);

const pagination: BaseTable.Pagination = reactive({
  currentPage: 1,
  perPage: 5,
  totalRows: 0,
});

function onOpenModal() {
  pagination.currentPage = 1;

  fetchAllCampaignSummary();
  fetchCampaignListSummary();
}

function hideModal() {
  emits('hide');
}

async function fetchAllCampaignSummary() {
  listAllCampaignSummary.value = [];
  loading.allCampaignSummary = true;
  fetchAnalyticsChartCampaignSummaryModel.payload.Channel = props.selectedChannel;
  fetchAnalyticsChartCampaignSummaryModel.payload.Filters = [
    {
      Key: 'startDate',
      Value: props.startDateTime,
    },
    {
      Key: 'endDate',
      Value: props.endDateTime,
    },
  ];

  await api
    .apiRequest(fetchAnalyticsChartCampaignSummaryModel)
    .then((response) => {
      const resp: Communication.DashboardByChannel.Response.AllCampaignSummary = response.data.result;
      console.log(resp);

      mapListAllCampaignSummary(resp);
    })
    .catch((err) => {
      openDefaultErrorModal(err);
    })
    .finally(() => {
      loading.allCampaignSummary = false;
    });
}

async function fetchCampaignListSummary() {
  listCampaignSummary.value = [];
  loading.campaignListSummary = true;
  fetchAnalyticsChartCampaignSummaryListModel.payload.Channel = props.selectedChannel;
  fetchAnalyticsChartCampaignSummaryListModel.payload.Filters = [
    {
      Key: 'startDate',
      Value: props.startDateTime,
    },
    {
      Key: 'endDate',
      Value: props.endDateTime,
    },
  ];
  fetchAnalyticsChartCampaignSummaryListModel.payload.Ordering = [];
  fetchAnalyticsChartCampaignSummaryListModel.payload.Page = pagination.currentPage;
  fetchAnalyticsChartCampaignSummaryListModel.payload.Limit = pagination.perPage;

  await api
    .apiRequest(fetchAnalyticsChartCampaignSummaryListModel)
    .then((response) => {
      listCampaignSummary.value = response.data.result.data_list;
      pagination.totalRows = response.data.result.filtered_record;
    })
    .catch((err) => {
      openDefaultErrorModal(err);
    })
    .finally(() => {
      loading.campaignListSummary = false;
    });
}

function mapListAllCampaignSummary(data: Communication.DashboardByChannel.Response.AllCampaignSummary) {
  const allCampaignSummary: SummaryLists[] = [
    {
      title: 'Campaign',
      value: data.campaign || 0,
      category: 'List',
    },
    {
      title: 'Sent',
      value: data.sent || 0,
      category: 'Message',
    },
    {
      title: 'Completed',
      value: data.complete || 0,
      category: 'Message',
    },
    {
      title: 'Fail',
      value: data.fail || 0,
      category: 'Message',
    },
  ];

  listAllCampaignSummary.value = allCampaignSummary;
}

function onClickCampaignName(data: Communication.DashboardByChannel.Response.CampaignListSummary) {
  switch (props.selectedChannel) {
    case CHANNEL_VALUE.EMAIL:
      return router.push({
        path: `communication/email/dashboard-by-campaign/${data.campaignid}`,
        query: {
          ...router.currentRoute.value.query,
        },
      });
    case CHANNEL_VALUE.SMS:
      return router.push({
        path: `communication/sms/dashboard-by-campaign/${data.campaignid}`,
        query: {
          ...router.currentRoute.value.query,
        },
      });
    case CHANNEL_VALUE.LINE:
      return router.push({
        path: `communication/line-oa/dashboard-by-campaign/${data.campaignid}`,
        query: {
          ...router.currentRoute.value.query,
        },
      });
    default:
      break;
  }
}

function resolveChannelIcon() {
  switch (props.selectedChannel) {
    case CHANNEL_VALUE.SMS:
      return ChannelSmsIcon;
    case CHANNEL_VALUE.EMAIL:
      return ChannelEDMIcon;
    case CHANNEL_VALUE.LINE:
      return ChannelLineIcon;
  }
}

function resolveChannelTitle() {
  switch (props.selectedChannel) {
    case CHANNEL_VALUE.SMS:
      return 'SMS';
    case CHANNEL_VALUE.EMAIL:
      return 'Email';
    case CHANNEL_VALUE.LINE:
      return 'Line OA';
  }
}
</script>

<style scoped lang="scss">
@import '@assets/styles/modules/communication/channel/dashboard-see-more-modal.scss';

:deep(.content-container) {
  .modal-inner {
    border-radius: 1.2rem !important;
    padding: 0 !important;
    .modal-header {
      font-size: 1rem;
    }
  }
}
</style>
